import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { slugify } from '@/utils/slugify';
import CategoryAccordion from '@/components/CategoryAccordion';
import CategoryFilterButton from '@/components/CategoryFilterButton';
import type { MouseEvent } from 'react';
import type { category } from '@/types/category';

interface CategoryFiltersProps {
  categories: category[];
  disabled?: boolean;
}

function CategoryFilters({ categories, disabled }: CategoryFiltersProps) {
  const params = useParams();
  const navigate = useNavigate();
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const featuredCategories = categories.filter((category) => category.featured !== false);
  const otherCategories = categories.filter((category) => category.featured === false);

  const hasActiveNonFeaturedCategory = (() => {
    const categorySlug = params.category;
    if (!categorySlug) return;
    return otherCategories.some((category) => slugify(category.name) === categorySlug);
  })();

  function handleToggleGroup(categoryTitle: string) {
    return function (event: MouseEvent) {
      event.preventDefault();
      const sluggedTitle = slugify(categoryTitle);
      setExpandedGroups((prev) => {
        if (prev.includes(sluggedTitle)) {
          return prev.filter((groupId) => groupId !== sluggedTitle);
        } else {
          return [...prev, sluggedTitle];
        }
      });
    };
  }

  function handleSubCategorySelect(categoryName: string, subCategoryName: string) {
    const sluggedCategoryName = slugify(categoryName);
    const sluggedSubCategoryName = slugify(subCategoryName);
    navigate(`/category/${sluggedCategoryName}/${sluggedSubCategoryName}`);
    setExpandedGroups([sluggedCategoryName]);
  }

  function handleOtherCategorySelect(categoryName: string) {
    const sluggedCategoryName = slugify(categoryName);
    navigate(`/category/${sluggedCategoryName}`);
    setExpandedGroups(['other']);
  }

  function isSubCategorySelected(categoryName: string, subCategoryName: string) {
    const { category, subCategory } = params;
    return category === slugify(categoryName) && subCategory === slugify(subCategoryName);
  }

  function isAllCategorySelected(categoryName: string) {
    const { category, subCategory } = params;
    return category === slugify(categoryName) && (subCategory === 'all' || !subCategory);
  }

  function isCategorySelected(categoryName: string) {
    const { category } = params;
    return category === slugify(categoryName);
  }

  useEffect(() => {
    const categorySlug = params.category;

    if (hasActiveNonFeaturedCategory) {
      setExpandedGroups(['other']);
    } else if (categorySlug) {
      setExpandedGroups([categorySlug]);
    }
  }, [params.category, hasActiveNonFeaturedCategory]);

  return (
    <div data-cy='category-filters' className='md:rounded-md md:shadow-md md:overflow-hidden h-min'>
      <h2 className='mb-0 p-3 px-4 bg-[#19393D] text-white text-base'>Filters</h2>

      <div className='border-y border-gray-300'>
        {featuredCategories.map((category) => {
          const slugifiedTitle = slugify(category.name);
          const isExpanded = expandedGroups.includes(slugifiedTitle);

          return (
            <CategoryAccordion
              key={category.id}
              id={category.id}
              title={category.name}
              isExpanded={isExpanded}
              toggleExpanded={handleToggleGroup(category.name)}
            >
              <ul className='p-4 px-7 pb-4 flex flex-col items-start gap-4'>
                <CategoryFilterButton
                  label='All'
                  disabled={disabled}
                  isActive={isAllCategorySelected(category.name)}
                  onClick={() => handleSubCategorySelect(category.name, 'all')}
                />

                {category.subcategories?.map((subCategory) => (
                  <CategoryFilterButton
                    disabled={disabled}
                    key={`filter-${subCategory.id}`}
                    label={subCategory.name}
                    isActive={isSubCategorySelected(category.name, subCategory.name)}
                    onClick={() => handleSubCategorySelect(category.name, subCategory.name)}
                  />
                ))}
              </ul>
            </CategoryAccordion>
          );
        })}

        {otherCategories.length > 0 && (
          <CategoryAccordion
            id='other'
            title='Other'
            isExpanded={expandedGroups.includes('other')}
            toggleExpanded={handleToggleGroup('other')}
          >
            <ul className='p-4 px-7 pb-4 flex flex-col items-start gap-4'>
              {otherCategories.map((category) => (
                <CategoryFilterButton
                  disabled={disabled}
                  label={category.name}
                  key={`other-${category.id}`}
                  isActive={isCategorySelected(category.name)}
                  onClick={() => handleOtherCategorySelect(category.name)}
                />
              ))}
            </ul>
          </CategoryAccordion>
        )}
      </div>
    </div>
  );
}

export default CategoryFilters;
